import { Alert, Card, CardContent, Typography } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import Grid from "@mui/material/Grid2";
import FolderIcon from "@mui/icons-material/Folder";
import { FolderSummary } from "../types";
import { formatDate, formatFileSize } from "../utils/formatters";
import { hasStatistics } from "../utils/folder-summary";

const StyledCard = styled(Card)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  height: "100%",
  padding: theme.spacing(2),
  cursor: "pointer",
  transition: "transform 0.2s ease, box-shadow 0.2s ease",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[6],
  },
  "&:active": {
    transform: "translateY(0) scale(0.98)",
    boxShadow: theme.shadows[2],
  },
  "&:focus-visible": {
    outline: `3px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
}));

interface FolderIconGridProps {
  folders: FolderSummary[];
  onViewFolder: (folder: FolderSummary) => void;
}

const FolderIconGrid = ({ folders, onViewFolder }: FolderIconGridProps) => {
  const theme = useTheme();

  if (!folders || folders.length === 0) {
    return <Alert severity="info">No folders found.</Alert>;
  }

  return (
    <Grid container spacing={2}>
      {folders.map((folder) => (
        <Grid key={folder.name} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
          <StyledCard
            variant="outlined"
            tabIndex={0}
            role="button"
            aria-label={`View files in ${folder.name}`}
            onClick={() => onViewFolder(folder)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onViewFolder(folder);
              }
            }}
          >
            <FolderIcon
              sx={{ fontSize: 56, color: theme.palette.primary.main, mb: 1 }}
            />
            <CardContent sx={{ padding: "0 !important", width: "100%" }}>
              <Typography variant="subtitle2" noWrap title={folder.name}>
                {folder.name}
              </Typography>
              {hasStatistics(folder) ? (
                <>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    {folder.fileCount} file{folder.fileCount === 1 ? "" : "s"}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    {formatFileSize(folder.totalSize)}
                  </Typography>
                  {!!folder.archivedCount && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      {folder.archivedCount} archived
                    </Typography>
                  )}
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    display="block"
                  >
                    As of {formatDate(folder.calculatedOn)}
                  </Typography>
                </>
              ) : (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Open to calculate
                </Typography>
              )}
            </CardContent>
          </StyledCard>
        </Grid>
      ))}
    </Grid>
  );
};

export default FolderIconGrid;
